import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { client, csrfHeader, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";

export type Card = components["schemas"]["Card"];
export type NewCard = components["schemas"]["CardRequest"];
export type CardImport = components["schemas"]["CardImport"];
export type CardImportChoice = components["schemas"]["CardImportChoice"];
export type CardImportDetail = components["schemas"]["CardImportDetail"];
export type CardImportPreview = components["schemas"]["CardImportPreview"];
export type CardImportResult = components["schemas"]["CardImportResult"];
export type CardBulkResult = components["schemas"]["CardBulkResult"];
export type CardBulkRequest = components["schemas"]["CardBulkRequest"];
export type ImportRun = components["schemas"]["CardImportRunRequest"];

/** Field -> messages, as the server words them. Empty when the card would save. */
export type FieldErrors = Record<string, string[]>;

export type CardFilters = {
  search: string;
  page: number;
  /** A trade show id, as text; empty for every show. */
  tradeShow: string;
  /** An import id, as text; empty for every source. */
  cardImport: string;
  /** A seller's id, as text. Manager-only, like the prospect book's. */
  agent: string;
};

export function useCards(filters: CardFilters) {
  return useQuery({
    queryKey: ["cards", filters],
    queryFn: () =>
      unwrap(
        client.GET("/api/cards/", {
          params: {
            query: {
              page: filters.page,
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.tradeShow ? { trade_show: Number(filters.tradeShow) } : {}),
              ...(filters.cardImport
                ? { card_import: Number(filters.cardImport) }
                : {}),
              ...(filters.agent ? { current_agent: Number(filters.agent) } : {}),
            },
          },
        }),
        t("errors.cards"),
      ),
    // The list stays on screen while the next page or search arrives.
    placeholderData: keepPreviousData,
  });
}

/** The three counts over the list: all of them, typed by hand, imported. */
export function useCardSummary(agent: string) {
  return useQuery({
    queryKey: ["cards", "summary", agent],
    queryFn: () =>
      unwrap(
        client.GET("/api/cards/summary/", {
          params: { query: agent ? { current_agent: Number(agent) } : {} },
        }),
        t("errors.cards"),
      ),
  });
}

/** Ask the server what it thinks of a draft. Never saves. */
export async function validateCard(body: NewCard, card?: number): Promise<FieldErrors> {
  const data = await unwrap(
    client.POST("/api/cards/validate/", {
      body,
      params: { query: card ? { card } : {} },
    }),
    t("errors.validateCard"),
  );
  return data.errors;
}

export function useSaveCard(card?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewCard) =>
      card
        ? unwrap(
            client.PATCH("/api/cards/{id}/", { params: { path: { id: card } }, body }),
            t("errors.saveCard"),
          )
        : unwrap(client.POST("/api/cards/", { body }), t("errors.saveCard")),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cards"] }),
  });
}

/** The table mode's save. Partial by design: good rows stay saved. */
export function useBulkCards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CardBulkRequest) =>
      unwrap(client.POST("/api/cards/bulk/", { body }), t("errors.saveCard")),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cards"] }),
  });
}

export function useCardImports(page: number) {
  return useQuery({
    queryKey: ["card-imports", page],
    queryFn: () =>
      unwrap(
        client.GET("/api/card-imports/", { params: { query: { page } } }),
        t("errors.cardImports"),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * The options of the list's *Importación* filter. A seller gets their own
 * imports — the server scopes them — and management everyone's, or one
 * agent's once the list is narrowed to that agent.
 */
export function useCardImportChoices(agent: string) {
  return useQuery({
    queryKey: ["card-imports", "choices", agent],
    queryFn: () =>
      unwrap(
        client.GET("/api/card-imports/choices/", {
          params: { query: agent ? { current_agent: Number(agent) } : {} },
        }),
        t("errors.cardImports"),
      ),
  });
}

export function useCardImport(id: number) {
  return useQuery({
    queryKey: ["card-import", id],
    queryFn: () =>
      unwrap(
        client.GET("/api/card-imports/{id}/", { params: { path: { id } } }),
        t("errors.cardImport"),
      ),
  });
}

/**
 * Upload a workbook. A `FormData` body, which `openapi-fetch` cannot express —
 * so `fetch` directly, with the CSRF header the client middleware would add.
 * A refusal carries the server's reason ("El archivo no es un Excel…").
 */
export function useUploadCardImport() {
  return useMutation({
    mutationFn: async ({ name, file }: { name: string; file: File }) => {
      const body = new FormData();
      body.append("name", name);
      body.append("file", file);
      const response = await fetch("/api/card-imports/", {
        method: "POST",
        credentials: "same-origin",
        headers: csrfHeader(),
        body,
      });
      const data: unknown = await response.json().catch(() => null);
      if (!response.ok) throw new Error(firstMessage(data) ?? t("errors.uploadCards"));
      return data as CardImportDetail;
    },
  });
}

export function useRunCardImport(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportRun) =>
      unwrap(
        client.POST("/api/card-imports/{id}/run/", { params: { path: { id } }, body }),
        t("errors.runImport"),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(["card-import", id], data);
      void queryClient.invalidateQueries({ queryKey: ["cards"] });
      void queryClient.invalidateQueries({ queryKey: ["card-imports"] });
    },
  });
}

/** `{"file": ["…"]}` -> "…". */
function firstMessage(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  for (const value of Object.values(body)) {
    if (typeof value === "string") return value;
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  }
  return null;
}
